import { useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
const logo = new URL("../../assets/mononites-logo.jpeg", import.meta.url).toString();

const nav = [
  { label: "About", href: "#about" },
  { label: "Our Services", href: "#services" },
  { label: "Directors", href: "#team" },
  { label: "Location", href: "#location" },
  { label: "Contact", href: "#contact" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      setOpen(false);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = nav.map((n) => n.href.slice(1));
    const observers = ids.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: "-35% 0px -55% 0px" }
      );
      observer.observe(el);
      return observer;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[var(--navy-deep)]/90 backdrop-blur-2xl border-b border-[var(--gold)]/20 shadow-[0_1px_0_0_oklch(0.78_0.15_78/12%)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="/" className="flex items-center gap-3 group">
          <div className="relative h-12 w-12 rounded-full overflow-hidden ring-2 ring-[var(--gold)]/40 group-hover:ring-[var(--gold)] transition-all duration-300 group-hover:shadow-[0_0_20px_oklch(0.78_0.15_78/30%)]">
            <img src={logo} alt="Mononites Food Services logo" className="h-full w-full object-cover" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-2xl text-gold tracking-wide">mononites</div>
            <div className="text-[8px] uppercase tracking-[0.3em] text-muted-foreground">Food Services</div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {nav.map((n) => {
            const isActive = activeSection === n.href.slice(1);
            return (
              <a
                key={n.href}
                href={n.href}
                className={`relative text-[11px] uppercase tracking-[0.18em] transition-colors pb-0.5 ${
                  isActive ? "text-gold" : "text-foreground/80 hover:text-gold"
                }`}
              >
                {n.label}
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-[var(--gold)] rounded-full" />
                )}
              </a>
            );
          })}
        </nav>

        <a
          href="/brochure.png"
          download="Mononites_brochure.png"
          className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-[var(--gold-foreground)] text-sm font-medium uppercase tracking-wider hover:shadow-gold transition-all hover:-translate-y-0.5 active:translate-y-0"
        >
          <Download size={14} />
          Download Brochure
        </a>

        <button
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className="md:hidden h-10 w-10 grid place-items-center rounded-full border border-[var(--gold)]/30 text-gold"
        >
          <span className="block w-4 h-px bg-current relative before:content-[''] before:absolute before:-top-1.5 before:left-0 before:right-0 before:h-px before:bg-current after:content-[''] after:absolute after:top-1.5 after:left-0 after:right-0 after:h-px after:bg-current" />
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[var(--navy-deep)]/95 backdrop-blur-2xl border-t border-[var(--gold)]/15">
          <div className="px-6 py-6 flex flex-col gap-5">
            {nav.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className={`text-sm uppercase tracking-[0.2em] transition-colors ${
                  activeSection === n.href.slice(1)
                    ? "text-gold"
                    : "text-foreground/90 hover:text-gold"
                }`}
              >
                {n.label}
              </a>
            ))}
            <a
              href="/brochure.png"
              download="Mononites_brochure.png"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-gold text-[var(--gold-foreground)] text-sm font-medium uppercase tracking-wider"
            >
              <Download size={14} />
              Download Brochure
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
